import { Card, DatePicker, Empty, Spin, Space } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { getFlowbackStats } from '@/api/requirementFlowback';
import type { FlowbackStatsItem } from '@/api/requirementFlowback';
import { getSubcategoryOptions, SUBCATEGORY_OPTIONS } from '@/constants/requirements';
import { useT } from '@/i18n';
import type { Translate } from '@/i18n';

const { RangePicker } = DatePicker;

type Dimension = 'subcategory' | 'initiator' | 'project';

const DIMENSIONS: { key: Dimension; title: string }[] = [
  { key: 'initiator', title: '回流排行榜' },
  { key: 'subcategory', title: '需求类别' },
  { key: 'project', title: '项目' },
];

// 构建单维度图表 option：发起人/项目降序取前 15 并补齐左对齐；需求类别显示全部类别（中文标签）
function buildOption(dimension: Dimension, stats: FlowbackStatsItem[], t: Translate) {
  let items: FlowbackStatsItem[];
  if (dimension === 'subcategory') {
    const statsMap = new Map(stats.map((s) => [s.key, s]));
    items = SUBCATEGORY_OPTIONS.map(({ key }) =>
      statsMap.get(key) ?? { key, count: 0, breakdown: { pending: 0, approved: 0, rejected: 0 } },
    );
  } else {
    items = [...stats]
      .sort((a, b) => b.breakdown.approved - a.breakdown.approved)
      .slice(0, 15);
    while (items.length < 15) {
      items.push({ key: '', count: 0, breakdown: { pending: 0, approved: 0, rejected: 0 } });
    }
  }
  const subMap: Record<string, string> = Object.fromEntries(
    getSubcategoryOptions(t).map(({ key, label }) => [key, label]),
  );
  return {
    tooltip: { trigger: 'axis' as const, axisPointer: { type: 'shadow' as const } },
    grid: { left: 48, right: 24, top: 24, bottom: 48 },
    xAxis: {
      type: 'category' as const,
      data: items.map((i) =>
        dimension === 'subcategory' ? subMap[i.key] || i.key || t('(未知)') : i.key || '',
      ),
      axisLabel: { rotate: items.length > 6 ? 30 : 0 },
    },
    yAxis: { type: 'value' as const, minInterval: 1 },
    series: [
      {
        name: t('已合入'),
        type: 'bar',
        data: items.map((i) => i.breakdown.approved),
        itemStyle: { color: '#52c41a' },
        barWidth: 40,
      },
    ],
  };
}

const FlowbackDashboard: React.FC = () => {
  const { t } = useT();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [dataByDim, setDataByDim] = useState<Record<Dimension, FlowbackStatsItem[]>>({
    initiator: [],
    subcategory: [],
    project: [],
  });
  const [loading, setLoading] = useState(false);

  // 日期对三者统一生效：并发请求三个维度
  useEffect(() => {
    const params: { date_from?: string; date_to?: string } = {};
    if (range) {
      params.date_from = range[0].format('YYYY-MM-DD');
      params.date_to = range[1].format('YYYY-MM-DD');
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFlowbackStats({ dimension: 'initiator', ...params }),
      getFlowbackStats({ dimension: 'subcategory', ...params }),
      getFlowbackStats({ dimension: 'project', ...params }),
    ])
      .then(([a, b, c]) => {
        if (cancelled) return;
        setDataByDim({ initiator: a.items, subcategory: b.items, project: c.items });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  const options = useMemo(
    () => ({
      initiator: buildOption('initiator', dataByDim.initiator, t),
      subcategory: buildOption('subcategory', dataByDim.subcategory, t),
      project: buildOption('project', dataByDim.project, t),
    }),
    [dataByDim, t],
  );

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <RangePicker
          value={range}
          onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
          allowClear
          presets={[
            { label: t('近7天'), value: [dayjs().subtract(7, 'day'), dayjs()] },
            { label: t('近30天'), value: [dayjs().subtract(30, 'day'), dayjs()] },
            { label: t('本周'), value: [dayjs().startOf('week'), dayjs().endOf('week')] },
            { label: t('本月'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
          ]}
        />
      </div>
      <Spin spinning={loading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {DIMENSIONS.map(({ key, title }) => {
            const showEmpty = dataByDim[key].length === 0 && key !== 'subcategory';
            return (
              <Card key={key} title={t(title)}>
                {showEmpty ? (
                  <Empty description={t('暂无回流数据')} style={{ padding: 48 }} />
                ) : (
                  <div style={{ height: 320, width: '100%' }}>
                    <ReactECharts option={options[key]} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />
                  </div>
                )}
              </Card>
            );
          })}
        </Space>
      </Spin>
    </div>
  );
};

export default FlowbackDashboard;
